import type { AttachmentCategory, ModelsType } from "@nlc/shared-types";

import { createContext, useMemo } from "react";

import type { ManagedFile } from "./types";

import { useFileUploader, type UseFileUploaderReturn } from "./useFileUploader";

export interface FileUploaderContextValue extends UseFileUploaderReturn {
  /** List of accepted file extensions */
  acceptedFileTypes?: string[];
  /** Whether multiple file selection is allowed */
  allowsMultiple: boolean;
}

export const FileUploaderContext =
  createContext<FileUploaderContextValue | null>(null);

/**
 * Props for the FileUploaderProvider
 */
interface FileUploaderProviderProps {
  /** List of accepted file extensions (e.g., ['.jpg', '.png']) */
  acceptedFileTypes?: string[];
  /** Allow multiple file selection (default: true) */
  allowsMultiple?: boolean;
  /** Category of attachment */
  category: AttachmentCategory;
  /** Children components */
  children: React.ReactNode;
  /** Marks the row as MBC-owned so it is not reaped while the brief is a draft. */
  isMbc?: boolean;
  /** Maximum number of files allowed (default: 150) */
  maxFiles?: number;
  /** Maximum file size in bytes (default: 200MB) */
  maxFileSize?: number;
  /** Called whenever the files array changes */
  onFilesChange?: (files: ManagedFile[]) => void;
  /** Called when all pending uploads complete with the successful attachment IDs */
  onUploadComplete?: (attachmentIds: number[]) => void;
  /** ID of the parent entity (e.g., requestId) */
  parentId: number;
  /** Type of parent entity */
  parentType: ModelsType;
  /** Prevent uploading files with the same name and extension (default: false) */
  preventDuplicates?: boolean;
}

export function FileUploaderProvider({
  acceptedFileTypes,
  allowsMultiple = true,
  category,
  children,
  isMbc,
  maxFiles,
  maxFileSize,
  onFilesChange,
  onUploadComplete,
  parentId,
  parentType,
  preventDuplicates,
}: FileUploaderProviderProps) {
  const uploaderState = useFileUploader({
    acceptedFileTypes,
    category,
    isMbc,
    maxFiles,
    maxFileSize,
    onFilesChange,
    onUploadComplete,
    parentId,
    parentType,
    preventDuplicates,
  });

  const contextValue = useMemo<FileUploaderContextValue>(
    () => ({
      ...uploaderState,
      acceptedFileTypes,
      allowsMultiple,
    }),
    [uploaderState, acceptedFileTypes, allowsMultiple],
  );

  return (
    <FileUploaderContext.Provider value={contextValue}>
      {children}
    </FileUploaderContext.Provider>
  );
}
